import React from "react";
import type { PaneMode } from "../../UnifiedBoard/UnifiedBoard";

export type GridView = "board" | "sequence" | "anchors";
export type SeqMode = "board" | "live";

interface ViewSignal {
  view: GridView;
  nonce: number;
}

export interface ViewState {
  view: GridView;
  setView: React.Dispatch<React.SetStateAction<GridView>>;
  paneMode: PaneMode;
  setPaneMode: React.Dispatch<React.SetStateAction<PaneMode>>;
  focusedLeaf: string | null;
  setFocusedLeaf: React.Dispatch<React.SetStateAction<string | null>>;
  trainTiesView: boolean;
  setTrainTiesView: React.Dispatch<React.SetStateAction<boolean>>;
  seqMode: SeqMode;
  setSeqMode: React.Dispatch<React.SetStateAction<SeqMode>>;
  seqHold: boolean;
  setSeqHold: React.Dispatch<React.SetStateAction<boolean>>;
  trainSeqMode: SeqMode;
  setTrainSeqMode: React.Dispatch<React.SetStateAction<SeqMode>>;
  selectedLane: { leafKey: string; lane: number } | null;
  setSelectedLane: React.Dispatch<
    React.SetStateAction<{ leafKey: string; lane: number } | null>
  >;
  goTab: (v: GridView) => void;
}

export function useViewState(viewSignal: ViewSignal | null): ViewState {
  // The workspace's three views over ONE draft: Board (zone × workday canvas
  // — the default), Dependencies (sequencing + logic), and Anchors (the
  // section's connections to the rest of the project, with review state).
  const [view, setView] = React.useState<GridView>("board");
  // The unified workspace shows the Takt board and the dependency gantt stacked
  // in ONE view; paneMode collapses to either or shows Both (board on top,
  // gantt on the bottom, drag-resizable). The board/gantt distinction in `view`
  // is now layout, not navigation — only "anchors" is a separate tab.
  const [paneMode, setPaneMode] = React.useState<PaneMode>("both");
  // Unified-board drill-in: the board leaf (level) the gantt is focused on. Null
  // ⇒ the gantt shows every level. Set by clicking a board zone header.
  const [focusedLeaf, setFocusedLeaf] = React.useState<string | null>(null);
  // True when the gantt is showing JUST the train (via "View Train Ties").
  // Any normal tab click clears it; goTab() centralizes that.
  const [trainTiesView, setTrainTiesView] = React.useState(false);
  // Dependencies tab mode: "board" shows every in-scope activity at its BOARD
  // dates (no forward pass display); "live" recalculates as you edit.
  const [seqMode, setSeqMode] = React.useState<SeqMode>("board");
  // True while an add row is being edited: the forward pass (graph refetch) is
  // held, and runs ONCE when the user hits Done / Cancel.
  const [seqHold, setSeqHold] = React.useState(false);
  // How the train lays out: "board" = your hand-placed arrangement (the safe
  // baseline you're modifying), "live" = a forward pass driven by the ties (a
  // preview of what your logic produces). Toggling back to "board" reverts the
  // layout so you can see what you had before.
  const [trainSeqMode, setTrainSeqMode] = React.useState<SeqMode>("board");
  // The selected lane — the tap-append target (and the rail's green glow).
  const [selectedLane, setSelectedLane] = React.useState<
    { leafKey: string; lane: number } | null
  >(null);

  const goTab = React.useCallback((v: GridView) => {
    setTrainTiesView(false);
    setView(v);
  }, []);

  const viewSignalNonce = viewSignal?.nonce;
  React.useEffect(() => {
    if (viewSignal) {
      setTrainTiesView(false);
      setView(viewSignal.view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewSignalNonce]);

  return {
    view,
    setView,
    paneMode,
    setPaneMode,
    focusedLeaf,
    setFocusedLeaf,
    trainTiesView,
    setTrainTiesView,
    seqMode,
    setSeqMode,
    seqHold,
    setSeqHold,
    trainSeqMode,
    setTrainSeqMode,
    selectedLane,
    setSelectedLane,
    goTab,
  };
}
